import { print } from "graphql";

import {
  LIST_ASSETS_WITH_DIFFERENT_LINKED_TEMPLATES,
  LIST_OWN_OEM_ASSETS_MISSING_SERIAL_NUMBERS_QUERY,
  LIST_OWN_OEM_ASSET_TEMPLATES_MISSING_TEMPLATE_IDS_QUERY,
  LIST_OWN_OEM_CUSTOMERS_MISSING_CLIENT_IDS_QUERY,
  LIST_OWN_OEM_DUPLICATE_SUPPORT_TICKETS,
  LIST_OWN_OEM_DUPLICATE_TICKETS,
  LIST_OWN_OEM_KANBAN_TICKETS,
  LIST_OWN_OEM_PARTS_INVALID_ARTICLE_NUMBERS_FOR_ASSET_QUERY,
  LIST_OWN_OEM_PROCEDURE_TEMPLATES_MISSING_IDS_QUERY,
  LIST_USERS_DUPLICATED_EMAILS_QUERY,
  CHECK_PARENT_CLIENT_IDS_QUERY,
  CHECK_PARENT_TICKET_IDS_QUERY,
  VALIDATE_PARENT_TICKET_ASSETS_QUERY,
  LIST_TEAMS_QUERY,
} from "~/api/_queries";
import { APP, AUTH, CLIENT_AUTH_REQUEST_TYPE, JWT } from "~/apollo/_config";

const endpoint =
  import.meta.env.VITE_APP_GRAPHQL_ENDPOINT ?? APP.ENDPOINT.GRAPHQL;

// FETCH HELPER
const fetchQuery = async ({ operationName = "", query, variables } = {}) => {
  const headers = {
    "content-type": "application/json",
    [AUTH.STRATEGIES.CLIENT.AUTH_HEADER]: CLIENT_AUTH_REQUEST_TYPE,
    [JWT.HEADER.TOKEN.NAME]:
      localStorage.getItem(JWT.LOCAL_STORAGE.TOKEN.NAME) || null,
    [JWT.HEADER.REFRESH_TOKEN.NAME]:
      localStorage.getItem(JWT.LOCAL_STORAGE.REFRESH_TOKEN.NAME) || null, // eslint-disable-line
  };

  const graphqlQuery = {
    operationName,
    query: print(query),
    variables,
  };

  const options = {
    method: "POST",
    headers,
    body: JSON.stringify(graphqlQuery),
  };

  const response = await fetch(endpoint, options);
  const data = await response.json();
  return data;
};

// FETCH QUERIES

export const fetchAllTeamNames = async () => {
  const names = new Set();
  const limit = 500;
  let skip = 0;
  let totalCount = Number.POSITIVE_INFINITY;

  while (skip < totalCount) {
    const response = await fetchQuery({
      query: LIST_TEAMS_QUERY,
      operationName: "listTeams",
      variables: {
        params: {
          limit,
          skip,
          where: {},
        },
      },
    });

    const listTeams = response?.data?.listTeams;
    (listTeams?.teams ?? []).forEach((team) => {
      if (team?.name) names.add(team.name);
    });
    totalCount = listTeams?.totalCount ?? 0;
    skip += limit;
  }

  return names;
};

export const fetchListOwnOemCustomersMissingClientIds = async (args) => {
  const { variables } = args || {};
  return fetchQuery({
    query: LIST_OWN_OEM_CUSTOMERS_MISSING_CLIENT_IDS_QUERY,
    operationName: "listOwnOemCustomersMissingClientIds",
    variables,
  });
};

export const fetchListOwnOemAssetsMissingSerialNumbers = async (args) => {
  const { variables } = args || {};
  return fetchQuery({
    query: LIST_OWN_OEM_ASSETS_MISSING_SERIAL_NUMBERS_QUERY,
    operationName: "listOwnOemAssetsMissingSerialNumbers",
    variables,
  });
};

export const fetchListOwnOemAssetTemplatesMissingTemplateIds = async (args) => {
  const { variables } = args || {};
  return fetchQuery({
    query: LIST_OWN_OEM_ASSET_TEMPLATES_MISSING_TEMPLATE_IDS_QUERY,
    operationName: "listOwnOemAssetTemplatesMissingTemplateIds",
    variables,
  });
};

export const fetchListOwnOemDuplicateTickets = async (args) => {
  const { variables } = args || {};
  return fetchQuery({
    query: LIST_OWN_OEM_DUPLICATE_TICKETS,
    operationName: "listOwnOemDuplicateTickets",
    variables,
  });
};

export const fetchListOwnOemDuplicateSupportTickets = async (args) => {
  const { variables } = args || {};
  return fetchQuery({
    query: LIST_OWN_OEM_DUPLICATE_SUPPORT_TICKETS,
    operationName: "listOwnOemDuplicateSupportTickets",
    variables,
  });
};

export const fetchListAssetsWithDifferentLinkedTemplates = async (args) => {
  const { variables } = args || {};
  return fetchQuery({
    query: LIST_ASSETS_WITH_DIFFERENT_LINKED_TEMPLATES,
    operationName: "listAssetsWithDifferentLinkedTemplates",
    variables,
  });
};

export const fetchListUsersDuplicatedEmails = async (args) => {
  const { variables } = args || {};
  return fetchQuery({
    query: LIST_USERS_DUPLICATED_EMAILS_QUERY,
    operationName: "listUsersDuplicatedEmails",
    variables,
  });
};

export const fetchListOwnOemProcedureTemplatesMissingIds = async (args) => {
  const { variables } = args || {};
  return fetchQuery({
    query: LIST_OWN_OEM_PROCEDURE_TEMPLATES_MISSING_IDS_QUERY,
    operationName: "listOwnOemProcedureTemplatesMissingIds",
    variables,
  });
};

export const fetchListOwnOemPartsInvalidArticleNumbersForAsset = async (
  args,
) => {
  const { variables } = args || {};
  return fetchQuery({
    query: LIST_OWN_OEM_PARTS_INVALID_ARTICLE_NUMBERS_FOR_ASSET_QUERY,
    operationName: "listOwnOemPartsInvalidArticleNumbersForAsset",
    variables,
  });
};

export const fetchListOwnOemOpenTicketsTotalCount = async ({
  closedStatusId,
} = {}) => {
  const res = await fetchQuery({
    query: LIST_OWN_OEM_KANBAN_TICKETS,
    operationName: "listOwnOemKanbanTickets",
    variables: {
      params: {
        limit: 1,
        skip: 0,
        sort: [],
        where: {},
      },
    },
  });
  const data = res?.data?.listOwnOemKanbanTickets;
  if (!data) return 0;

  const totalCount = data.totalCount ?? 0;
  const closedId = String(closedStatusId ?? "").trim();
  if (!closedId) return totalCount;

  const columns = Array.isArray(data.columns) ? data.columns : [];
  if (!columns.length) return totalCount;

  const openCount = columns
    .filter((c) => String(c?.status?._id ?? "") !== closedId)
    .reduce((sum, c) => sum + (Number(c?.totalCount) || 0), 0);

  return openCount;
};

export const fetchCheckParentClientIds = async (args) => {
  const { variables } = args || {};
  return fetchQuery({
    query: CHECK_PARENT_CLIENT_IDS_QUERY,
    operationName: "checkParentClientIds",
    variables,
  });
};

export const fetchCheckParentTicketIds = async (args) => {
  const { variables } = args || {};
  return fetchQuery({
    query: CHECK_PARENT_TICKET_IDS_QUERY,
    operationName: "checkParentTicketIds",
    variables,
  });
};

export const fetchValidateParentTicketAssets = async (args) => {
  const { variables } = args || {};
  return fetchQuery({
    query: VALIDATE_PARENT_TICKET_ASSETS_QUERY,
    operationName: "validateParentTicketAssets",
    variables,
  });
};
